/**
 * iSteelElement Main Application Component
 */

import '../../../ui-styles/ibest-style.css';
import { useMemo } from 'react';
import { SUPPORT_TYPES, defineFields, mergeInitialValues, useAnalysisWorkflow, useAppStorage, useCalcId, useRHFWithZod, useSavedCalculationLoader, useUrlFormValues } from '../shared';
import { analyze } from './api';
import type { APIResponse } from './appConfig';
import { APP_NAME, APP_VERSION, APP_VERSION_POLICY, SECTION_NAMES, RESPONSE_DISPLAY_CONFIG } from './appConfig';
import { schema } from './validation';
import type { FormValues, ParsedValues } from './validation';
import { ProjectInput, PrintPortal, FormFields, ResponseDisplay, GenerateLinkButton } from '../shared';
import { generateShareableURL } from '../shared/urlUtils';

export default function App() {
  // Check for calcId first (short URL from CalcPad)
  const calcId = useCalcId();
  const urlFormValues = useUrlFormValues(calcId);

  const initialValues: FormValues = useMemo(
    () =>
      mergeInitialValues(
        {
          project: 'Default',
          title: 'Metal Stud',
          L: 15,
          W: '16/12',
          addedWeight: '5',
          support: 1,
          sectionName: SECTION_NAMES[0].label,
          numStuds: 1,
          restraintLength: 4,
          axialLoad: '0',
          pressureHistory: '4,0,0',
          timeHistory: '0,14,30',
          u0: 0,
          v0: 0
        },
        urlFormValues
      ),
    [urlFormValues]
  );

  const {
    register,
    formState: { errors },
    watch,
    reset
  } = useRHFWithZod<FormValues>({ schema, defaultValues: initialValues });

  const values = watch();
  const { saveDataToDB } = useAppStorage<FormValues, APIResponse>(APP_NAME, APP_VERSION);
  const { analysisResult, setAnalysisResult, isAnalyzing, saveStatus, handleAnalyze, handleSave } = useAnalysisWorkflow<FormValues, ParsedValues, APIResponse>({
    errors,
    values,
    schema,
    analyze,
    saveDataToDB
  });

  // Load full state from IndexedDB if calcId is present
  useSavedCalculationLoader<FormValues, APIResponse>(calcId, reset, setAnalysisResult, { appName: APP_NAME, versionPolicy: APP_VERSION_POLICY });

  const fields = defineFields<FormValues>([
    { kind: 'input', name: 'title', label: 'Title', helpText: 'Enter the title of the component', placeholder: 'Component Title' },
    { kind: 'input', name: 'L', label: 'Length', unit: 'ft', helpText: 'Enter the length of the component', placeholder: 'Component Length' },
    {
      kind: 'input',
      name: 'W',
      label: 'Tributary Width',
      unit: 'ft',
      helpText: 'Enter the tributary width of the component',
      placeholder: 'Component Tributary Width'
    },
    {
      kind: 'input',
      name: 'addedWeight',
      label: 'Added Weight',
      unit: 'psf',
      helpText: 'Enter the added weight on the component',
      placeholder: 'Component Added Weight'
    },
    { kind: 'dropdown', name: 'support', label: 'Support', options: SUPPORT_TYPES, helpText: 'Enter the support type...' },
    { kind: 'dropdown', name: 'sectionName', label: 'Section Name', options: SECTION_NAMES, helpText: 'Enter the Section Name...' },
    { kind: 'input', name: 'numStuds', label: 'Number of Studs', unit: '', helpText: 'Number of Studs', placeholder: 'Number of Studs' },
    { kind: 'input', name: 'restraintLength', label: 'Restraint Length', unit: 'ft', helpText: 'Restraint Length', placeholder: 'Restraint Length' },
    { kind: 'input', name: 'axialLoad', label: 'Axial Load', unit: 'kips', helpText: 'Axial Load', placeholder: 'Axial Load' },
    { kind: 'input', name: 'pressureHistory', label: 'Pressure History', unit: 'psi', helpText: 'Pressure History', placeholder: 'Pressure History' },
    { kind: 'input', name: 'timeHistory', label: 'Time History', unit: 'msec', helpText: 'Time History', placeholder: 'Time History' },
    { kind: 'input', name: 'u0', label: 'Initial Displacement', unit: 'in', helpText: 'Initial Displacement', placeholder: 'Initial Displacement' },
    { kind: 'input', name: 'v0', label: 'Initial Velocity', unit: 'in/msec', helpText: 'Initial Velocity', placeholder: 'Initial Velocity' }
  ]);
  return (
    <>
      <div className="analysis-app analysis-app--teal analysis-app__layout layout-3col print:hidden w-full">
        <div className="analysis-app__hero">
          <div>
            <h2 className="analysis-app__title">{APP_NAME}</h2>
            <p className="analysis-app__subtitle">Metal Stud Analysis</p>
          </div>
        </div>
        <div className="analysis-app__controls flex flex-col rounded-xl p-1 m-1 gap-y-0 shadow-div">
          <ProjectInput error={errors.project?.message ?? null} {...register('project')} />

          <FormFields<FormValues> fields={fields} register={register} errors={errors} />

          <div className="analysis-app__actions flex flex-row w-full mt-1 gap-2">
            <button className="submit-button" onClick={handleAnalyze} disabled={Object.keys(errors).length > 0 || isAnalyzing}>
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </button>
            <button className="submit-button" onClick={handleSave} disabled={Object.keys(errors).length > 0 || saveStatus === 'saving'}>
              {saveStatus === 'saving' ? 'Saving...' : saveStatus === 'success' ? 'Saved!' : saveStatus === 'error' ? 'Error!' : 'Save'}
            </button>
            <GenerateLinkButton formData={values} />
          </div>
        </div>

        <div className="analysis-app__results-wrap grid grid-rows-[auto_1fr] rounded-xl gap-y-0 w-fit">
          <div className="analysis-app__results shadow-div rounded-xl m-1">
            {/* The Steel Element Type response goes here  */}
            <ResponseDisplay data={analysisResult} sections={RESPONSE_DISPLAY_CONFIG} emptyMessage="No analysis results yet." />
          </div>

          <div className="analysis-app__charts flex flex-col shadow-div my-2 m-1 rounded-xl">
            {analysisResult?.charts && (
              <div className="analysis-app__charts-grid grid grid-cols-2 gap-2 m-5 print:gap-1">
                {Object.entries(analysisResult.charts)
                  .filter(([, v]) => !!v)
                  .map(([key, v]) => {
                    const chart = v!;
                    const src = `data:${chart.mime};base64,${chart.base64}`;

                    return (
                      <div key={key} className="analysis-app__chart-card break-inside-avoid">
                        <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
        <div className="print:hidden hidden lg:block"></div>
      </div>

      {/* ---- PRINT PORTAL ---- */}
      <PrintPortal>
        <div className="hidden print:block print-root ">
          <div className="print-a4-landscape border border-black box-border bg-white">
            <div className="hidden print:grid h-full min-h-0 grid-cols-[24%_76%] print:bg-white print:text-black">
              <div className="flex h-full min-h-0 flex-col">
                <div className="p-1">
                  <div className="text-[10px] font-semibold m-0 p-0 leading-none">{values.title || 'iSteel Element'}</div>
                  <div className="text-[10px] opacity-70 m-0 p-0 leading-none mb-1">Project: {values.project || '—'}</div>

                  <div className="m-0 p-0 ">
                    <table className="w-full table-fixed text-[10px] m-0 p-0">
                      <colgroup>
                        <col className="w-55%]" />
                        <col className="w-[45%]" />
                      </colgroup>

                      <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Inputs</caption>

                      <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                        {(
                          [
                            ['Length', `${values.L} ft`],
                            ['Tributary Width', `${values.W} ft`],
                            ['Added Weight', `${values.addedWeight} psf`],
                            ['Support', values.support ? SUPPORT_TYPES.find(opt => opt.value === values.support)?.label : '—'],
                            ['Section', values.sectionName],
                            ['Number of Studs', values.numStuds],
                            ['Restrained Length', `${values.restraintLength} ft`],
                            ['Axial Load', `${values.axialLoad} kips`],
                            ['u0', `${values.u0} in`],
                            ['v0', `${values.v0} in/msec`]
                          ] as Array<[React.ReactNode, React.ReactNode]>
                        ).map(([label, value], i) => (
                          <tr key={i}>
                            <td className="font-semibold">{label}</td>
                            <td>{value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="overflow-hidden border-t">
                  <div className="p-1">
                    {analysisResult && (
                      <table className="w-full table-fixed text-[10px] m-0 p-0">
                        <colgroup>
                          <col className="w-[55%]" />
                          <col className="w-[45%]" />
                        </colgroup>

                        <caption className="caption-top text-[10px] font-semibold m-0 p-0 leading-none">Results</caption>

                        <tbody className="[&>tr>td]:p-1 [&>tr>td]:leading-none">
                          {[
                            [
                              `Effective Mass`,
                              <span>
                                {analysisResult.Me} lbf-in/sec<sup>2</sup>
                              </span>
                            ],
                            ['Disp (Max/Min)', `${analysisResult.Xmax} / ${analysisResult.Xmin} in`],
                            ['Steel Weight', `${analysisResult.steel_weight} lbs`],
                            ['Resistance (Reb/Inb)', `${analysisResult.Ru[0]} / ${analysisResult.Ru[1]} kips`],
                            ['Ductility (Reb/Inb)', `${analysisResult.duct[0]} / ${analysisResult.duct[1]}`],
                            ['Rotation (Reb/Inb)', `${analysisResult.rot[0]} / ${analysisResult.rot[1]} deg`],
                            ['Support 1 (Reb/Inb)', `${analysisResult.V1[0]} / ${analysisResult.V1[1]} kips`],
                            ['Support 2 (Reb/Inb)', `${analysisResult.V2[0]} / ${analysisResult.V2[1]} kips`]
                          ].map(([label, value], i) => (
                            <tr key={i}>
                              <td className="font-semibold">{label}</td>
                              <td>{value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Shareable Link at Bottom */}
                <div className="mt-auto p-1 border-t">
                  <a href={generateShareableURL(values, window.location.pathname)} className="text-[9px] text-blue-600 underline font-semibold hover:text-blue-800" target="_blank" rel="noopener noreferrer" title="Click to open shareable link">
                    🔗 LINK
                  </a>
                </div>
              </div>

              <div className="h-full min-h-0 overflow-hidden border-l">
                <div className="h-full min-h-0 pl-2">
                  {analysisResult?.charts && (
                    <div className="grid grid-cols-2 gap-2 print:gap-1 m-2 h-full">
                      {Object.entries(analysisResult.charts)
                        .filter(([, v]) => !!v)
                        .map(([key, v]) => {
                          const chart = v!;
                          const src = `data:${chart.mime};base64,${chart.base64}`;

                          return (
                            <div key={key} className="break-inside-avoid">
                              <img src={src} alt={`${key} chart`} loading="lazy" className="w-full h-auto object-contain rounded-none max-w-250" />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PrintPortal>
    </>
  );
}
